import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, formatDateTime } from "@/lib/status";
import { PackageCheck, RefreshCw, Camera, BellRing, Search, X, RotateCw, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReminderDialog from "@/components/ReminderDialog";

// Two segments: fresh orders awaiting delivery/first check vs. items that need a
// follow-up action (returns / exchanges / partial arrivals).
const SEGMENTS = {
  orders: { label: "Bekleyen Siparişler", statuses: ["Sipariş Verildi", "Kontrol Bekliyor"], Icon: PackageCheck },
  returns: { label: "İade / Değişim / Kısmi", statuses: ["Kısmi Geldi", "İade Bekleniyor", "Değişim Bekleniyor"], Icon: Repeat },
};

export default function WarehouseInboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderReq, setReminderReq] = useState(null);
  const [segment, setSegment] = useState("orders");
  const [q, setQ] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox/warehouse");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Reset status filter when switching segments (statuses differ per segment).
  const switchSegment = (s) => { setSegment(s); setStatusFilter(""); };

  const segCounts = useMemo(() => {
    const c = { orders: 0, returns: 0 };
    for (const r of items) {
      if (SEGMENTS.orders.statuses.includes(r.status)) c.orders += 1;
      else if (SEGMENTS.returns.statuses.includes(r.status)) c.returns += 1;
    }
    return c;
  }, [items]);

  // Suppliers present in the current segment (for the dropdown).
  const supplierOptions = useMemo(() => {
    const set = new Set();
    items.filter((r) => SEGMENTS[segment].statuses.includes(r.status))
      .forEach((r) => { if (r.supplier_name) set.add(r.supplier_name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
  }, [items, segment]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((r) => {
      if (!SEGMENTS[segment].statuses.includes(r.status)) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (supplierFilter && r.supplier_name !== supplierFilter) return false;
      if (term) {
        const its = (r.items && r.items.length > 0) ? r.items : [{ product_code: r.product_code, product_name: r.product_name }];
        const hay = [
          r.request_no, r.supplier_name,
          ...its.flatMap((i) => [i.product_code, i.product_name]),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [items, segment, q, supplierFilter, statusFilter]);

  const hasFilters = q || supplierFilter || statusFilter;
  const clearFilters = () => { setQ(""); setSupplierFilter(""); setStatusFilter(""); };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <PackageCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Depo Kontrolü</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              <span data-testid="warehouse-count" className="font-medium text-slate-900">{items.length}</span> sipariş kontrol bekliyor
            </p>
          </div>
        </div>
        <Button variant="outline" data-testid="refresh-btn" onClick={load} className="h-10 gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
      </div>

      {/* Segment tabs */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 mb-3 w-full sm:w-auto">
        {Object.entries(SEGMENTS).map(([key, seg]) => {
          const active = segment === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`segment-${key}`}
              onClick={() => switchSegment(key)}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${active ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              <seg.Icon className="h-4 w-4" />
              {seg.label}
              <span data-testid={`segment-${key}-count`} className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>
                {segCounts[key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 grid grid-cols-1 sm:grid-cols-12 gap-2">
        <div className="sm:col-span-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="warehouse-search"
            placeholder="Talep no, ürün adı/kodu, tedarikçi ara..."
            className="pl-9 h-11"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button
              data-testid="warehouse-search-clear"
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-slate-100 text-slate-400"
              aria-label="Aramayı temizle"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="sm:col-span-3">
          <Select value={supplierFilter || "__all__"} onValueChange={(v) => setSupplierFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="warehouse-filter-supplier" className="h-11">
              <SelectValue placeholder="Tüm Tedarikçiler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tüm Tedarikçiler</SelectItem>
              {supplierOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-3">
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="warehouse-filter-status" className="h-11">
              <SelectValue placeholder="Tüm Durumlar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tüm Durumlar</SelectItem>
              {SEGMENTS[segment].statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasFilters && (
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-slate-500">
            <span data-testid="warehouse-filtered-count" className="font-medium text-slate-900">{filtered.length}</span> sonuç
          </div>
          <button
            data-testid="warehouse-clear-filters"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 rounded-full px-2 py-1"
          >
            <RotateCw className="h-3 w-3" /> Filtreleri temizle
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-400 py-12">Yükleniyor...</div>
      ) : filtered.length === 0 ? (
        <div data-testid="empty-warehouse" className="text-center py-16 border border-dashed border-slate-200 rounded-lg bg-white">
          <PackageCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <div className="text-slate-700 font-medium">
            {hasFilters ? "Aramanızla eşleşen kayıt yok" : (segment === "orders" ? "Kontrol bekleyen sipariş yok" : "Bekleyen iade/değişim yok")}
          </div>
          <div className="text-sm text-slate-400 mt-1">
            {hasFilters ? "Filtreleri değiştirin veya temizleyin." : (segment === "orders" ? "Tedarikçiden sipariş gelince burada görünür." : "İade veya değişim talebi oluştuğunda burada görünür.")}
          </div>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((r) => {
            const its = (r.items && r.items.length > 0) ? r.items : [{
              product_code: r.product_code, product_name: r.product_name,
              quantity: r.quantity, unit: r.unit,
            }];
            const openCount = (r.receipt?.items || []).filter((i) => !(i.done || i.resolved)).length;
            return (
              <div key={r.id} data-testid={`warehouse-row-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Link to={`/talepler/${r.id}`} className="font-display font-semibold text-slate-900 hover:underline">
                      {r.request_no}
                    </Link>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-sm text-slate-700 truncate">
                    {its.length > 1 ? `${its.length} kalem` : its[0]?.product_name}
                    {openCount > 0 && (
                      <span data-testid={`pending-${r.id}`} className="ml-2 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        {openCount} kalem açık
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {formatDateTime(r.updated_at || r.created_at)} • {r.supplier_name}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "Kısmi Geldi" && (r.receipt?.items || []).some((i) => !(i.done || i.resolved)) && (
                    <Button
                      data-testid={`remind-btn-${r.id}`}
                      variant="outline"
                      onClick={() => setReminderReq(r)}
                      className="h-10 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      <BellRing className="h-4 w-4" /> Hatırlat
                    </Button>
                  )}
                  <Link to={`/talepler/${r.id}/kontrol`} data-testid={`receipt-link-${r.id}`}>
                    <Button className="h-10 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Camera className="h-4 w-4" /> Kontrol Et
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ReminderDialog
        open={!!reminderReq}
        onOpenChange={(v) => { if (!v) setReminderReq(null); }}
        request={reminderReq || {}}
        onDone={load}
      />
    </div>
  );
}
